import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Loading } from 'components';
import {
	useForm,
	useFieldArray,
	SubmitHandler,
	useWatch,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputControlled, SelectControlled, Icon, Button } from 'components';
import { NewPatientStep } from '../NewPatientStep';
import { showError } from 'utils/alerts';
import { useParams, useNavigate } from 'react-router-dom';
import { INTITUTION_TYPES, diseaseAssessment_OPTIONS, scheme_OPTIONS } from 'utils/constants';
import { formatDateToTimeZoneDate } from 'utils/functions';
import {
	editPatientTreatments,
	newPatientTreatments,
	deletePatientTreatment,
	deletePatientTreatmentDrug,
} from 'services/patients';
import { PatientContext } from 'contexts/PatientContext';
import cn from 'classnames';
import Swal from 'sweetalert2';
import './index.css';
import {
	Accordion,
	AccordionItem,
	AccordionItemHeading,
	AccordionItemButton,
	AccordionItemPanel,
} from 'react-accessible-accordion';
import { toast } from 'react-toastify';
import { useAuth } from 'contexts/AuthContext';

interface TreatmentsPageProps { }

interface IFormInputs {
	treatments: [
		{
			id?: number | string | undefined;
			order: string;
			scheme: string;
			schemeName?: string;
			startDate: Date | string;
			drugs: Array<DrugType>;
			toxicities: string;
			diseaseAssessment: string;
			medulogram: string;
			medulogramDate: Date | string;
			EMR: string;
			EMRDate: Date | string;
			EMRBlasts?: number;
			clinicalMonitoring: string;
		}
	];
}

export const Treatments: React.FunctionComponent<TreatmentsPageProps> = () => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const isBioBancoInstitution = user.doctor.institution.type === INTITUTION_TYPES['BIOBANK'];
	const { isAdmin } = user;
	const { patientId } = useParams();
	const [loading, setLoading] = useState(false);
	const { patient, setPatient, fetching, fetchPatient } =
		useContext<any>(PatientContext);
	const [expandedItem, setExpandedItem] = useState(-1);
	const navigate = useNavigate();

	const schema = yup.object().shape({
		treatments: yup.array().of(
			yup.object().shape({
				order: yup.string().required(t('error.requiredField')),
				scheme: yup.string(),
				schemeName: yup.string().when('scheme', {
					is: 'Other',
					then: yup.string().required(t('error.requiredField')),
				}),
				startDate: yup
					.date()
					.nullable()
					.default(undefined)
					.transform((curr, orig) => (orig === '' ? null : curr))
					.typeError(t('error.mustChooseADate')),
				drugs: yup.array().of(
					yup.object().shape({
						name: yup.string(),
						dose: yup.string(),
						startApplicationDate: yup
							.date()
							.nullable()
							.default(undefined)
							.transform((curr, orig) => (orig === '' ? null : curr))
							.typeError(t('error.mustChooseADate')),
						lastAplicationDate: yup
							.date()
							.nullable()
							.default(undefined)
							.transform((curr, orig) => (orig === '' ? null : curr))
							.typeError(t('error.mustChooseADate')),
					})
				),
				toxicities: yup.string(),
				diseaseAssessment: yup.string(),
				medulogram: yup.string(),
				medulogramDate: yup
					.date()
					.nullable()
					.default(undefined)
					.transform((curr, orig) => (orig === '' ? null : curr))
					.typeError(t('error.mustChooseADate')),
				EMR: yup.string(),
				EMRDate: yup
					.date()
					.nullable()
					.default(undefined)
					.transform((curr, orig) => (orig === '' ? null : curr))
					.typeError(t('error.mustChooseADate')),
				EMRBlasts: yup.number().nullable(),
				clinicalMonitoring: yup.string(),
			})
		),
	});

	const {
		handleSubmit,
		control,
		getValues,
		formState: { errors, isValid, dirtyFields },
	} = useForm<IFormInputs>({
		resolver: yupResolver(schema),
		mode: 'onChange',
	});

	const {
		fields: treatments,
		append,
		update,
		replace,
		remove,
	} = useFieldArray({
		control,
		name: 'treatments',
		keyName: 'key',
	});

	useEffect(() => {
		if (!isBioBancoInstitution &&
			Object.keys(patient).length !== 0 &&
			(patient?.diagnostics?.length === 0 ||
				!patient?.diagnostics[0]?.consent ||
				patient?.diagnostics[0]?.dxSubType?.includes('Other'))
		) {
			navigate('../final');
		}

		patient?.treatments && replace(patient.treatments);
	}, [patient]);

	const addNewTreatment = () => {
		append({
			order: (treatments.length + 1).toString(),
			scheme: '',
			startDate: undefined,
			drugs: [
				{
					name: '',
					dose: '',
					lastAplicationDate: undefined,
				},
			],
			toxicities: '',
			diseaseAssessment: '',
			medulogram: '',
			medulogramDate: undefined,
			EMR: '',
			EMRDate: undefined,
			EMRBlasts: undefined,
			clinicalMonitoring: '',
		});
		setExpandedItem(treatments.length);
	};

	const addNewDrug = (treatmentIndex: number) => {
		const newDrug: DrugType = {
			name: '',
			dose: 0,
			lastAplicationDate: undefined,
		};
		const treatmentsValues = getValues();
		const currentTreatment = treatmentsValues.treatments[treatmentIndex];
		update(treatmentIndex, {
			...currentTreatment,
			drugs: [...currentTreatment.drugs, newDrug],
		});
		// setExpandedItem(treatmentIndex);
	};

	const removeDrug = (
		treatmentIndex: number,
		drugIndex: number,
		drugId: number | string | undefined
	) => {
		if (drugId) {
			Swal.fire({
				title: t('areYouSure'),
				text: t('doYouWantToDeleteDose'),
				footer: t('youCanNotUndoThisAction'),
				icon: 'warning',
				showCancelButton: true,
			}).then(async (result: any) => {
				if (result.isConfirmed) {
					try {
						setLoading(true);
						await deletePatientTreatmentDrug(drugId);
						let drugs = [...treatments[treatmentIndex].drugs];
						drugs.splice(drugIndex, 1);
						update(treatmentIndex, {
							...treatments[treatmentIndex],
							drugs: [...drugs],
						});
						await fetchPatient();
					} catch (error) {
						showError(t('error.savePatientInfo'));
					}
					setLoading(false);
				}
			});
		} else {
			let drugs = [...treatments[treatmentIndex].drugs];
			drugs.splice(drugIndex, 1);
			update(treatmentIndex, {
				...treatments[treatmentIndex],
				drugs: [...drugs],
			});
		}
	};

	const removeTreatment = (
		treatmentIndex: number,
		treatmentId: number | string
	) => {
		if (treatmentId) {
			Swal.fire({
				title: t('areYouSure'),
				text: t('doYouWantToDeleteTreatment'),
				footer: t('youCanNotUndoThisAction'),
				icon: 'warning',
				showCancelButton: true,
			}).then(async (result: any) => {
				if (result.isConfirmed) {
					try {
						setLoading(true);
						await deletePatientTreatment(treatmentId);
						const _treatments = treatments.splice(treatmentIndex, 1);
						setPatient({ ...patient, treatments: [..._treatments] });
						remove(treatmentIndex);
						await fetchPatient();
					} catch (error) {
						showError(t('error.savePatientInfo'));
					}
					setLoading(false);
				}
			});
		} else {
			remove(treatmentIndex);
		}
	};

	const handleOnSubmit: SubmitHandler<IFormInputs> = async (data) => {
		try {
			const modifiedFields = Object.keys(dirtyFields);
			if (modifiedFields.length > 0 && data.treatments.length > 0) {
				if (patientId && patient.treatments.length > 0) {
					let formattedData = data.treatments.map((t) => ({
						id: t.id,
						order: t.order,
						scheme: t.scheme.includes('Other')
							? `Other: ${t.schemeName}`
							: t.scheme,
						startDate: t.startDate,
						drugs: t.drugs.map((drug) => ({
							id: drug.id,
							order: drug.order,
							name: drug.name,
							dose: drug.dose,
							lastAplicationDate: drug.lastAplicationDate,
							startApplicationDate: drug.startApplicationDate,
						})),
						toxicities: t.toxicities,
						diseaseAssessment: t.diseaseAssessment,
						medulogram: t.medulogram,
						medulogramDate: t.medulogramDate,
						EMR: t.EMR,
						EMRDate: t.EMRDate,
						EMRBlasts: t.EMRBlasts,
						clinicalMonitoring: t.clinicalMonitoring,
					}));
					await editPatientTreatments(patient.id, formattedData);
				} else {
					let formattedData = data.treatments.map((t) => ({
						order: t.order,
						scheme: t.scheme.includes('Other')
							? `Other: ${t.schemeName}`
							: t.scheme,
						startDate: t.startDate,
						drugs: t.drugs,
						toxicities: t.toxicities,
						diseaseAssessment: t.diseaseAssessment,
						medulogram: t.medulogram,
						medulogramDate: t.medulogramDate,
						EMR: t.EMR,
						EMRDate: t.EMRDate,
						EMRBlasts: t.EMRBlasts,
						clinicalMonitoring: t.clinicalMonitoring,
					}));
					await newPatientTreatments({
						patientId: patient.id,
						treatments: [...formattedData],
					});
				}
				setPatient({ ...patient, treatments: [...data.treatments] });
				toast.success(t('patientEdited'));
				await fetchPatient();
			}
		} catch (error) {
			showError(t('error.savePatientInfo'));
		}
	};

	const expandItem = (index: number) => {
		if (index === expandedItem) {
			setExpandedItem(-1);
		} else {
			setExpandedItem(index);
		}
	};

	const watching = useWatch({ control, name: 'treatments' });

	return (
		<NewPatientStep
			title={`04.- ${t('treatments')}`}
			prevStep='/diagnostic'
			nextStep='/lab-report'
			handleOnClickNext={handleSubmit(handleOnSubmit)}
			isValid={isValid}
		>
			{fetching || loading ? (
				<Loading />
			) : (
				<form
					className='relative block'
					onSubmit={handleSubmit(handleOnSubmit)}
				>
					{watching && (
						<Accordion
							allowZeroExpanded
							className='w-full'
						>
							{treatments?.map((treatment, index) => (
								<AccordionItem
									key={treatment.key}
									className='my-6 treatment-accordion'
									activeClassName='my-6 treatment-accordion opened'
									dangerouslySetExpanded={expandedItem === index}
								>
									<AccordionItemHeading onClick={() => expandItem(index)}>
										<AccordionItemButton
											className={cn(
												'py-6 text-orange-light text-lg flex justify-between items-center'
											)}
										>
											<p>{`${t('treatment')} ${index + 1}`}</p>
											<div className='flex items-center'>
												{!isAdmin && (
													<Icon
														className='p-3 mr-2'
														name='Trash'
														//@ts-ignore
														onClick={() => removeTreatment(index, treatment.id)}
													/>
												)}
												<Icon name='ArrowSmall' />
											</div>
										</AccordionItemButton>
									</AccordionItemHeading>
									<AccordionItemPanel className='w-full pb-6'>
										<div className='grid lg:grid-cols-12 grid-cols-12 gap-6'>
											<InputControlled
												name={`treatments[${index}].order`}
												disabled={isAdmin}
												control={control}
												defaultValue={index + 1}
												className='hidden'
											/>
											<SelectControlled
												name={`treatments[${index}].scheme`}
												disabled={isAdmin}
												control={control}
												className={cn({
													'lg:col-span-7':
														watching[index]?.scheme.includes('Other'),
													'lg:col-span-9':
														!watching[index]?.scheme.includes('Other'),
												})}
												labelClass='sm'
												defaultValue={
													treatments[index].scheme.includes('Other')
														? {
															value: 'Other',
															label: t('other'),
														}
														: treatments[index].scheme
															? {
																value: treatments[index].scheme,
																label: treatments[index].scheme,
															}
															: ''
												}
												options={scheme_OPTIONS}
												label={t('scheme')}
												//@ts-ignore
												errors={errors.scheme}
											/>
											{watching[index]?.scheme.includes('Other') && (
												<InputControlled
													name={`treatments[${index}].schemeName`}
													disabled={isAdmin}
													control={control}
													defaultValue={treatments[index].scheme.slice(6)}
													className={'lg:col-span-2'}
													labelClass='sm'
													label={`${t('schemeName')}`}
													//@ts-ignore
													errors={errors?.name}
												/>
											)}

											<InputControlled
												name={`treatments[${index}].startDate`}
												disabled={isAdmin}
												control={control}
												defaultValue={
													treatments[index]?.startDate
														? formatDateToTimeZoneDate(
															treatments[index]?.startDate
														)
														: undefined
												}
												className='lg:col-span-3'
												labelClass='sm'
												type='date'
												label={t('startDate')}
												//@ts-ignore
												errors={errors?.startDate}
											/>

											{treatments[index]?.drugs?.map((drug, i) => (
												<div
													key={treatment.key + i}
													className='relative block col-span-12 w-full'
												>
													<div className='grid col-span-6 grid-cols-12 gap-6 items-start relative'>
														<InputControlled
															name={`treatments[${index}].drugs[${i}].name`}
															disabled={isAdmin}
															control={control}
															defaultValue={drug?.name || ''}
															className='lg:col-span-4'
															labelClass='sm'
															placeholder='Lorem Ipsum'
															label={`${t('drug')} N${i + 1}`}
															//@ts-ignore
															errors={errors?.name}
														/>
														<InputControlled
															name={`treatments[${index}].drugs[${i}].dose`}
															disabled={isAdmin}
															control={control}
															defaultValue={drug?.dose || ''}
															className='lg:col-span-2'
															labelClass='sm'
															label={t('dose')}
															placeholder='1000'
															//@ts-ignore
															errors={errors?.dose}
														/>
														<InputControlled
															name={`treatments[${index}].drugs[${i}].startApplicationDate`}
															disabled={isAdmin}
															control={control}
															defaultValue={
																treatments[index]?.drugs[i]?.startApplicationDate
																	? formatDateToTimeZoneDate(
																		treatments[index]?.drugs[i]?.startApplicationDate as string
																	)
																	: undefined
															}
															className='lg:col-span-3'
															labelClass='sm'
															type='date'
															label={t('startDate')}
															//@ts-ignore
															errors={errors?.treatments?.[index]?.drugs?.[i]?.startApplicationDate}
														/>
														<InputControlled
															name={`treatments[${index}].drugs[${i}].lastAplicationDate`}
															disabled={isAdmin}
															control={control}
															//@ts-ignore
															defaultValue={
																treatments[index]?.drugs[i].lastAplicationDate
																	? formatDateToTimeZoneDate(
																		//@ts-ignore
																		treatments[index].drugs[i]
																			.lastAplicationDate
																	)
																	: undefined
															}
															className='lg:col-span-3'
															labelClass='sm'
															type='date'
															label={t('lastAplicationDate')}
															//@ts-ignore
															errors={errors?.lastAplicationDate}
														/>
														{/* @ts-ignore */}
														{!isAdmin && (
															<div className='absolute flex items-center flex-row h-full justify-center -right-8'>
																<Icon
																	className='mt-4'
																	name='Trash'
																	onClick={() => removeDrug(index, i, drug.id)}
																/>
															</div>
														)}
													</div>
												</div>
											))}
											{!isAdmin && (
												<div className='flex justify-start lg:col-span-5'>
													<Button handleClick={() => addNewDrug(index)}>
														+ {t('newDose')}
													</Button>
												</div>
											)}
											<InputControlled
												name={`treatments[${index}].toxicities`}
												disabled={isAdmin}
												control={control}
												defaultValue={treatments[index].toxicities}
												className='lg:col-span-12'
												labelClass='sm'
												label={t('toxicities')}
												//@ts-ignore
												errors={errors.toxicities}
											/>
											<InputControlled
												name={`treatments[${index}].medulogram`}
												disabled={isAdmin}
												control={control}
												defaultValue={treatments[index].medulogram}
												className='lg:col-span-8'
												labelClass='sm'
												label={t('medulogram')}
												//@ts-ignore
												errors={errors?.medulogram}
											/>
											<InputControlled
												name={`treatments[${index}].medulogramDate`}
												disabled={isAdmin}
												control={control}
												defaultValue={
													treatments[index]?.medulogramDate
														? formatDateToTimeZoneDate(
															treatments[index]?.medulogramDate
														)
														: undefined
												}
												className='lg:col-span-4'
												labelClass='sm'
												type='date'
												label={t('medulogramDate')}
												//@ts-ignore
												errors={errors?.medulogramDate}
											/>
											<InputControlled
												name={`treatments[${index}].EMR`}
												disabled={isAdmin}
												control={control}
												defaultValue={treatments[index].EMR}
												className='lg:col-span-6'
												labelClass='sm'
												rows={1}
												label={t('EMR')}
												type='textarea'
												//@ts-ignore
												errors={errors?.EMR}
											/>
											<InputControlled
												name={`treatments[${index}].EMRBlasts`}
												disabled={isAdmin}
												control={control}
												defaultValue={treatments[index].EMRBlasts}
												className='lg:col-span-2'
												labelClass='sm'
												label={t('EMRBlasts')}
												type='number'
												//@ts-ignore
												errors={errors?.EMRBlasts}
											/>
											<InputControlled
												name={`treatments[${index}].EMRDate`}
												disabled={isAdmin}
												control={control}
												defaultValue={
													treatments[index]?.EMRDate
														? formatDateToTimeZoneDate(
															treatments[index]?.EMRDate
														)
														: undefined
												}
												className='lg:col-span-4'
												labelClass='sm'
												type='date'
												label={t('EMRDate')}
												//@ts-ignore
												errors={errors?.EMRDate}
											/>

											<SelectControlled
												name={`treatments[${index}].diseaseAssessment`}
												disabled={isAdmin}
												control={control}
												className='lg:col-span-12'
												labelClass='sm'
												defaultValue={
													treatments[index].diseaseAssessment
														? {
															value: treatments[index].diseaseAssessment,
															label: treatments[index].diseaseAssessment,
														}
														: ''
												}
												options={diseaseAssessment_OPTIONS}
												label={t('diseaseAssessment')}
												//@ts-ignore
												errors={errors.diseaseAssessment}
											/>
											<InputControlled
												name={`treatments[${index}].clinicalMonitoring`}
												disabled={isAdmin}
												control={control}
												defaultValue={treatments[index].clinicalMonitoring}
												className='lg:col-span-12'
												labelClass='sm'
												label={t('clinicalMonitoring')}
												type='textarea'
												//@ts-ignore
												errors={errors?.clinicalMonitoring}
											/>
										</div>
									</AccordionItemPanel>
								</AccordionItem>
							))}
						</Accordion>
					)}
					{!isAdmin && (
						<div className='flex justify-start'>
							<Button handleClick={addNewTreatment}>
								+ {t('newTreatment')}
							</Button>
						</div>
					)}
				</form>
			)}
		</NewPatientStep>
	);
};
